<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Categorias de Produtos</h5>
      <b-button
        v-if="hasPermission('adicionar_categorias_produtos')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill" title="Nova Categoria"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.nome"
        placeholder="Filtrar por Nome"
        class="form-control"
        @input="filtrar"
      />
      <b-button
        variant="secondary"
        @click="limparFiltros"
        class="d-flex align-items-center"
      >
        <i class="bi bi-funnel" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th><button class="btn btn-link p-0" @click="sort('nome')">Nome {{ arrow('nome') }}</button></th>
          <th><button class="btn btn-link p-0" @click="sort('ativo')">Ativo {{ arrow('ativo') }}</button></th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="cat in categorias" :key="cat.id">
          <td>{{ cat.nome }}</td>
          <td>{{ cat.ativo ? 'Sim' : 'Não' }}</td>
          <td class="text-end">
            <i v-if="hasPermission('detalhar_categorias_produtos')" class="bi bi-eye-fill text-primary mx-2" title="Detalhar" @click="detalhar(cat.id)"></i>
            <i v-if="hasPermission('alterar_categorias_produtos')" class="bi bi-pencil-fill text-warning mx-2" title="Editar" @click="editar(cat.id)"></i>
            <i v-if="hasPermission('excluir_categorias_produtos')" class="bi bi-trash-fill text-danger mx-2" title="Excluir" @click="excluir(cat.id)"></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <!-- 1. Resumo -->
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>

      <!-- 2. Seletor de itens por página -->
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limitePorPagina"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="onChangeLimit"
        />
      </div>

      <!-- 3. Controle de página -->
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="paginaAtual"
          :per-page="limitePorPagina"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, getCurrentInstance, computed, watch, ref } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import { useSortableTable } from '@/utils/useSortableTable'

const router = useRouter()
const { proxy } = getCurrentInstance()

// composable para categorias
const {
  items: categorias,
  totalItems,
  page: paginaAtual,
  limit: limitePorPagina,
  filters,
  load,
  sort,
  arrow
} = useSortableTable(
  `/cadastros/categorias-de-produtos`,
  { key: 'nome', order: 'asc' }
)

// opções de limite de páginas
const pageOptions = ref([10, 15, 20, 25, 50])

// Computeds para resumo
const startRow = computed(() =>
  totalItems.value === 0 ? 0 : (paginaAtual.value - 1) * Number(limitePorPagina.value) + 1
)
const endRow = computed(() =>
  totalItems.value === 0 ? 0 : Math.min(paginaAtual.value * Number(limitePorPagina.value), totalItems.value)
)

// Watchers para recarregar
watch(paginaAtual, () => load())
watch(limitePorPagina, () => {
  paginaAtual.value = 1
  load()
})

// Inicialização
onMounted(() => {
  filters.value = {
    nome: '',
    ativo: undefined
  }
  // garante valor numérico inicial para evitar "all"
  if (!Number(limitePorPagina.value)) limitePorPagina.value = 10
  load()
})

// Filtros
function filtrar() {
  paginaAtual.value = 1
  load()
}
function limparFiltros() {
  filters.value = {
    nome: '',
    ativo: undefined
  }
  paginaAtual.value = 1
  load()
}

// Handlers de paginação
function onChangeLimit() {
  paginaAtual.value = 1
  load()
}
function onPageChange() {
  load()
}

// Navegação e ações
function irParaCadastro() { router.push('/cadastros/categorias-de-produtos/novo') }
function detalhar(id)       { router.push(`/cadastros/categorias-de-produtos/${id}/detalhar`) }
function editar(id)         { router.push(`/cadastros/categorias-de-produtos/${id}`) }
async function excluir(id) {
  const ok = await proxy.$confirm('Confirma exclusão da categoria?','Confirmação')
  if (!ok) return
  await axios.delete(`/cadastros/categorias-de-produtos/${id}`)
  load()
  proxy.$toast.success('Categoria excluída')
}
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
</style>
